import { Link } from "react-router-dom"
import { createReducer } from "reduxsauce"
import { takeLatest, put, select } from "redux-saga/effects"
import pluralize from "pluralize"
import { chain, size, get, includes, indexOf } from "lodash"
import actionCreator from "helpers/actionCreator"
import { Creators as notificationCreators } from "modules/notifications/model"
import { getMissingReferenceEquity } from "./selectors/data"
import { FOUR_WAY_SORT_ORDERS } from "helpers/sorts"

export const { Types, Creators } = actionCreator("ompnt/modules/compareComposition/", {
  setMetric: ["metric"],
  setGroup: ["group"],
  setPositionsDisplayType: {
    props: ["positionsDisplayType"],
    track: true,
    trackType: "experiments-composition-positions-display",
    trackPayload: ({ positionsDisplayType }) => ({ positionsDisplayType }),
  },
  setPositionsSorting: ["positionsSorting"],
  showPositionsPage: ["ev", "page"],
  showMissingEquityDates: ["missingEquityItemsDates"],
  showMissingEquityDatesSuccess: ["missingReferenceEquity"],
})

const DEFAULT_STATE = {
  selectedMetric: null,
  selectedGroup: null,
  positionsDisplayType: null,
  positionsSorting: null,
  positionsPage: 0,
  missingReferenceEquity: [],
}

function handleSetMetric(state, { metric }) {
  return { ...state, selectedMetric: metric }
}

function handleSetGroup(state, { group }) {
  return { ...state, selectedGroup: group }
}

function handleSetPositionsDisplayType(state, { positionsDisplayType }) {
  return { ...state, positionsDisplayType }
}

function handleSetPositionsSorting(state, { positionsSorting }) {
  const currentSorting = state.positionsSorting
  const sorting = { by: positionsSorting }

  if (currentSorting?.order && currentSorting.by === positionsSorting) {
    const current = indexOf(FOUR_WAY_SORT_ORDERS, currentSorting.order)
    // Loop through the orders array
    const next = current + 1 < FOUR_WAY_SORT_ORDERS.length ? current + 1 : 0
    sorting.order = FOUR_WAY_SORT_ORDERS[next]
  } else {
    sorting.order = FOUR_WAY_SORT_ORDERS[0]
  }

  return { ...state, positionsSorting: sorting }
}

function handleShowPositionsPage(state, { page }) {
  return { ...state, positionsPage: page }
}

function handleShowMissingEquityDatesSuccess(state, { missingReferenceEquity }) {
  return { ...state, missingReferenceEquity }
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_METRIC]: handleSetMetric,
  [Types.SET_GROUP]: handleSetGroup,
  [Types.SET_POSITIONS_DISPLAY_TYPE]: handleSetPositionsDisplayType,
  [Types.SET_POSITIONS_SORTING]: handleSetPositionsSorting,
  [Types.SHOW_POSITIONS_PAGE]: handleShowPositionsPage,
  [Types.SHOW_MISSING_EQUITY_DATES_SUCCESS]: handleShowMissingEquityDatesSuccess,
})

function* handleShowMissingEquityDates({ missingEquityItemsDates }) {
  const { account, lastMissingReferenceEquity } = yield select(state => ({
    lastMissingReferenceEquity: getMissingReferenceEquity(state),
    account: state.global.selected.account,
  }))

  for (let i = 0; i < size(missingEquityItemsDates); i++) {
    const currentItemSize = size(missingEquityItemsDates[i].dates)
    if (currentItemSize > 0 && !get(lastMissingReferenceEquity, i, false)) {
      const overThreshold = currentItemSize - 10
      const dates = chain(missingEquityItemsDates[i].dates).take(10).join(", ").value()
      // [TODO] [#184018795] JSX shouldn't be in selectors or models
      const reupload = includes(["swap", "portfolio"], missingEquityItemsDates[i].type) ? <div><Link to={`/${account}/p/${missingEquityItemsDates[i].id}/upload`}>Re-upload</Link> positions and equity for these dates</div> : ""
      const message = <div>Reference Equity data is missing on {pluralize("date", size(dates))} {dates}{overThreshold > 0 ? ` and ${overThreshold} more ${pluralize("date", overThreshold)}` : ""} for {missingEquityItemsDates[i].label}. {reupload}</div>

      yield put(notificationCreators.add("error", null, message))
    }
  }

  yield put(Creators.showMissingEquityDatesSuccess(chain(missingEquityItemsDates)
    .map(elem => size(elem.dates) > 0)
    .value()))
}

export function* saga() {
  yield takeLatest(Types.SHOW_MISSING_EQUITY_DATES, handleShowMissingEquityDates)
}
